import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickViewModal = ({ ebook, isOpen, onClose, onAddToCart }) => {
  if (!isOpen || !ebook) return null;

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={`full-${i}`} name="Star" size={18} color="var(--color-accent)" className="fill-current" />
      );
    }

    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="StarHalf" size={18} color="var(--color-accent)" className="fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={18} color="var(--color-muted-foreground)" />
      );
    }

    return stars;
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-600"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-700 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Quick Preview
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-smooth focus-ring"
              aria-label="Close preview"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          <div className="p-4 md:p-6 lg:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="space-y-4">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={ebook?.coverImage}
                    alt={ebook?.coverImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {renderRatingStars(ebook?.rating)}
                  </div>
                  <span className="text-sm font-data text-muted-foreground">
                    {ebook?.rating?.toFixed(1)} ({ebook?.reviewCount} reviews)
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                    {ebook?.title}
                  </h3>
                  <p className="text-base text-muted-foreground font-caption">
                    by {ebook?.author}
                  </p>
                </div>

                <p className="text-base text-foreground/90 leading-relaxed">
                  {ebook?.description}
                </p>

                <div className="space-y-3">
                  <h4 className="text-sm font-heading font-semibold text-foreground uppercase tracking-wide">
                    Key Frameworks
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {ebook?.frameworks?.map((framework, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-md text-sm font-caption"
                      >
                        <Icon name="Target" size={14} />
                        {framework}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-heading font-semibold text-foreground uppercase tracking-wide">
                    Table of Contents Preview
                  </h4>
                  <ul className="space-y-2">
                    {ebook?.tableOfContents?.slice(0, 5)?.map((chapter, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-foreground/80">
                        <Icon name="ChevronRight" size={16} className="flex-shrink-0 mt-0.5 text-primary" />
                        <span>{chapter}</span>
                      </li>
                    ))}
                    {ebook?.tableOfContents?.length > 5 && (
                      <li className="text-sm text-muted-foreground italic">
                        +{ebook?.tableOfContents?.length - 5} more chapters
                      </li>
                    )}
                  </ul>
                </div>

                <div className="pt-6 border-t border-border space-y-4">
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                      ${ebook?.price?.toFixed(2)}
                    </span>
                    {ebook?.originalPrice && (
                      <span className="text-lg text-muted-foreground line-through font-data">
                        ${ebook?.originalPrice?.toFixed(2)}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="default"
                      size="lg"
                      iconName="ShoppingCart"
                      iconPosition="left"
                      onClick={() => {
                        onAddToCart(ebook);
                        onClose();
                      }}
                      fullWidth
                    >
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={onClose}
                      fullWidth
                    >
                      View Full Details
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickViewModal;